import React, { useMemo, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { ExpiredOverlay, CartStatusEnum, BaseCartItem, CartItem } from '@pegasus/cart';
import { LanguageCode, Translations } from '@pegasus/translation';
import { Header } from '@pegasus/ui-headers';
import { ReserveAgainItem } from '@pegasus/ui-headers';

import { Timer } from 'src/client/components/Header/cart/components/timer';
import { extendCart, removeItem, addItemToCart } from 'src/client/api/services/cart';
import { fetchCart, resetCart } from 'src/client/components/Header/cart/redux/cart';
import { manageAddToCartError } from 'src/client/components/Header/cart/helpers';
import { useCartWithPrefetch } from 'src/client/components/Header/cart/redux/hooks';

import {
    addErrorFlashMessage,
    addSuccessFlashMessage,
    addWarningFlashMessage,
} from 'src/client/components/shared/flash-messages';

import { CountrySelector } from 'src/client/components/shared/country-selector';
import { useConfig } from 'src/client/hoc/config';

type OwnProps = {
    translations: Translations;
    supportedLanguages: {
        url: string;
        countryCode: string;
        active: boolean;
    }[];
};

export const UserHeader = ({ translations, supportedLanguages }: OwnProps) => {
    const config = useConfig();
    const dispatch = useDispatch();
    const { cart, recoveredCart } = useCartWithPrefetch();
    const [timerState, setTimerState] = useState<CartStatusEnum | undefined>(undefined);

    const renderTimer = useMemo(
        () => () => (
            <Timer
                cart={cart}
                onCartExpiresFirstWarning={() => {
                    setTimerState(CartStatusEnum.CART_EXPIRE_IN_5_MINUTES);
                }}
                onCartExpiresSecondWarning={() => {
                    setTimerState(CartStatusEnum.CART_EXPIRE_IN_SHORT_PERIOD);
                }}
                onCartExpiresLastWarning={() => {
                    setTimerState(CartStatusEnum.CART_EXPIRE_IN_SHORT_PERIOD);
                }}
                onCartExpired={() => {
                    setTimerState(CartStatusEnum.CART_EXPIRED);
                }}
            />
        ),
        [cart],
    );

    const removeCartItem = useCallback(
        (simpleSku: string): Promise<void> => {
            const item = cart && cart.items.find((i: CartItem) => i.simpleSku === simpleSku);
            return removeItem({ simpleSku }).then(() => {
                dispatch(fetchCart());
            });
        },
        [cart],
    );

    const reserveAgain = useCallback(
        async ({ successFlashMessage = '', ...item }: ReserveAgainItem) => {
            try {
                await addItemToCart(item).then(({ error }: { error: any }) => {
                    if (error) {
                        return Promise.reject(manageAddToCartError(error));
                    }
                });
                await dispatch(fetchCart());
                if (successFlashMessage) {
                    dispatch(addSuccessFlashMessage(successFlashMessage));
                }
            } catch (error) {
                if (error.errorMessage) {
                    dispatch(addErrorFlashMessage(error.errorMessage));
                } else if (error.warningMessage) {
                    dispatch(addWarningFlashMessage(error.warningMessage));
                }
            }
        },
        [],
    );

    const updateItemImage = <T extends BaseCartItem>(item: T) => ({
        ...item,
        imageUrl: `${config.url.items}${item.imageUrl}?imwidth=62`,
    });
    return (
        <>
            <Header
                /* eslint-disable-next-line global-require */
                translations={translations}
                languageCode={translations.locale as LanguageCode}
                csrfToken={typeof window !== 'undefined' ? window.CSRF : ''}
                cart={
                    cart
                        ? {
                              items: cart.items.map(updateItemImage),
                              recoveredItems:
                                  recoveredCart && recoveredCart.items.map(updateItemImage),
                              info: cart,
                          }
                        : undefined
                }
                cartEvents={{
                    onItemRemove: removeCartItem,
                    onItemReserveAgain: reserveAgain,
                }}
                timer={renderTimer}
                supportedLanguages={supportedLanguages}
                isPrive={config.isPrive}
                showSuccessMessage={() => {}}
            />
            <CountrySelector translations={translations} />
            <ExpiredOverlay
                translations={translations}
                isShown={!!timerState}
                status={timerState || CartStatusEnum.CART_EXPIRED}
                closeDialog={() => {
                    if (timerState === CartStatusEnum.CART_EXPIRED) {
                        dispatch(resetCart());
                    }

                    setTimerState(undefined);
                }}
                extendCart={() => {
                    extendCart().then(() => {
                        dispatch(fetchCart());
                        setTimerState(undefined);
                    });
                }}
            />
        </>
    );
};
